import { useState, useEffect, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import {
  Download, 
  Info, 
  Maximize2,
  FileText,
  Eye,
  X,
  Image,
  BarChart3,
  Calendar,
  Layers
} from 'lucide-react';
import { getRequest } from '../services/apiService';

ChartJS.register(...registerables);

const chartColors = {
  system: 'rgba(79, 70, 229, 0.85)',
  user: 'rgba(16, 185, 129, 0.85)',
  actual: 'rgba(245, 158, 11, 0.85)',
  border: 'rgba(255, 255, 255, 0.5)'
};

const ALL_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const PhysicalProgressChart = ({ projectId}) => {
 
  const [showModal, setShowModal] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState('10470');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartHeight, setChartHeight] = useState(500);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [rawData, setRawData] = useState({});
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        const newHeight = width < 640 ? 400 : width < 1024 ? 450 : 500;
        setChartHeight(newHeight);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

useEffect(() => {
    const fetchTasks = async () => {
      if (!projectId) return;
      
      try {
        const response = await getRequest(`/pert/task-transaction-api/?parent_task_id=null&project_id=${projectId}`);
        if (response && response.data) {
          setTasks(response.data);
          // Set default selected task to first task if available
          if (response.data.length > 0 && !selectedTask) {
            setSelectedTask(response.data[0].id.toString());
          }
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
        setTasks([]);
      }
    };

    fetchTasks();
  }, [projectId]);
  useEffect(() => {
    const fetchChartData = async () => {
      if (!projectId || !selectedTask) return;
      
      setLoading(true);
      try {
        const response = await getRequest(
          `/transaction/monthly-charts/?task_id=${selectedTask}&ship_id=${projectId}`
        );
        if (response) {
          setRawData(response.data);
          const years = Object.keys(response.data).sort();
          setAvailableYears(years);
          if (years.length > 0) setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
        setRawData({});
      } finally {
        setLoading(false);
      }
    };
    fetchChartData();
  }, [projectId, selectedTask]);

  useEffect(() => {
    if (!selectedYear || !rawData[selectedYear]) {
      setChartData([]);
      return;
    }

    const yearData = rawData[selectedYear];
    const months = new Set();
    const tasksData = [];

    Object.entries(yearData).forEach(([taskName, taskData]) => {
      if (['type', 'scope', 'summary'].includes(taskName)) return;

      if (selectedMonth) {
        const monthEntry = taskData.details?.find(d => 
          d.month.toLowerCase() === selectedMonth.toLowerCase() && 
          d.year.toString() === selectedYear
        );
        if (monthEntry) {
          tasksData.push({
            task: taskName,
            system: monthEntry.system,
            user: monthEntry.user,
            actual: monthEntry.actual
          });
        }
      } else {
        tasksData.push({
          task: taskName,
          system_planned: taskData.system_planned_percent,
          user_planned: taskData.user_planned_percent,
          actual_completion: taskData.actual_completion_percent
        });
      }

      taskData.details?.forEach(d => {
        if (d.year.toString() === selectedYear) {
          months.add(d.month);
        }
      });
    });

    setAvailableMonths(Array.from(months).sort((a, b) => 
      new Date(`${selectedYear} ${a}`) - new Date(`${selectedYear} ${b}`)
    ));
    setChartData(tasksData);
  }, [selectedYear, selectedMonth, rawData]);

  const getDynamicYMax = () => {
    if (chartData.length === 0) return 20;

    const maxValue = Math.max(
      ...chartData.flatMap(item => 
        selectedMonth 
          ? [item.system, item.user, item.actual]
          : [item.system_planned, item.user_planned, item.actual_completion]
      )
    );

    const calculatedMax = Math.ceil((maxValue + 10) / 10) * 10;
    return Math.min(Math.max(calculatedMax, 20), 100);
  };

  const handleExportImage = async () => {
    if (!chartRef.current) return;
    
    try {
      const canvas = chartRef.current.canvas;
      const link = document.createElement('a');
      link.download = 'Physical_Progress_chart.png';
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Error exporting image:', error);
    }
  };

  const handleExportCSV = () => {
    if (chartData.length === 0) {
      alert('No data available to export');
      return;
    }

    const headers = selectedMonth 
      ? ['Task', 'System', 'User', 'Actual']
      : ['Task', 'System Planned', 'User Planned', 'Actual Completion'];
  
    const csvRows = chartData.map(entry => {
      if (selectedMonth) {
        return `"${entry.task.replace(/"/g, '""')}",${entry.system},${entry.user},${entry.actual}`;
      }
      return `"${entry.task.replace(/"/g, '""')}",${entry.system_planned},${entry.user_planned},${entry.actual_completion}`;
    });

    const csvContent = [
      headers.join(','),
      ...csvRows
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const filename = `progress_${projectId}_${selectedTask}_${selectedYear}${selectedMonth ? `_${selectedMonth}` : ''}.csv`;
    
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getChartConfig = () => ({
    labels: chartData.map(item => item.task),
    datasets: selectedMonth ? [
      {
        label: 'System',
        data: chartData.map(item => item.system),
        backgroundColor: chartColors.system,
        borderColor: chartColors.border,
        borderWidth: 1,
        borderRadius: 6,
        barThickness: 40,
        maxBarThickness: 50
      },
      {
        label: 'User',
        data: chartData.map(item => item.user),
        backgroundColor: chartColors.user,
        borderColor: chartColors.border,
        borderWidth: 1,
        borderRadius: 6,
        barThickness: 40,
        maxBarThickness: 50
      },
      {
        label: 'Actual',
        data: chartData.map(item => item.actual),
        backgroundColor: chartColors.actual,
        borderColor: chartColors.border,
        borderWidth: 1,
        borderRadius: 6,
        barThickness: 40,
        maxBarThickness: 50
      }
    ] : [
      {
        label: 'System Planned',
        data: chartData.map(item => item.system_planned),
        backgroundColor: chartColors.system,
        borderColor: chartColors.border,
        borderWidth: 1,
        borderRadius: 6,
        barThickness: 40,
        maxBarThickness: 50
      },
      {
        label: 'User Planned',
        data: chartData.map(item => item.user_planned),
        backgroundColor: chartColors.user,
        borderColor: chartColors.border,
        borderWidth: 1,
        borderRadius: 6,
        barThickness: 40,
        maxBarThickness: 50
      },
      {
        label: 'Actual Completion',
        data: chartData.map(item => item.actual_completion),
        backgroundColor: chartColors.actual,
        borderColor: chartColors.border,
        borderWidth: 1,
        borderRadius: 6,
        barThickness: 40,
        maxBarThickness: 50
      }
    ]
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          color: '#4b5563',
          font: { size: 11 },
          maxRotation: 0,
          minRotation: 0,
          callback: function(value, index, values) {
            const label = this.getLabelForValue(value);
            // Truncate long labels and add ellipsis if needed
            return label.length > 15 ? label.substring(0, 15) + '...' : label;
          }
        },
        grid: { display: false }
      },
      y: {
        beginAtZero: true,
        max: getDynamicYMax(),
        min: 0,
        ticks: {
          color: '#4b5563',
          callback: (value) => `${value}%`,
          stepSize: 10
        },
        grid: { color: 'rgba(243, 244, 246, 0.8)' }
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#374151',
          usePointStyle: true,
          pointStyle: 'rectRounded',
          padding: 20,
          font: { size: 14, weight: 'bold' }
        }
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#1f2937',
        bodyColor: '#374151',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        callbacks: {
          title: (context) => {
            // Show full task name in tooltip
            const dataIndex = context[0].dataIndex;
            return chartData[dataIndex]?.task || '';
          },
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y || 0;
            return `${label}: ${value}%`;
          }
        }
      }
    },
    categoryPercentage: 0.7,
    barPercentage: 0.8,
    layout: {
      padding: {
        left: 10,
        right: 10,
        bottom: 20
      }
    }
  };

  const renderChart = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-indigo-600 font-medium">Loading chart data...</p>
          </div>
        </div>
      );
    }

    if (!chartData.length) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <BarChart3 size={48} className="text-gray-400 mb-4" />
          <p className="text-lg">No data available for selected parameters</p>
          <p className="text-sm mt-2">Try selecting different filters or time periods</p>
        </div>
      );
    }

    // Calculate dynamic width based on number of data points
    const minBarWidth = 120;
    const calculatedWidth = Math.max(chartData.length * minBarWidth, 800);

    return (
      <div className="w-full bg-white p-6 rounded-xl shadow-md border border-gray-100">
        {/* Sticky Legend */}
        <div className="sticky left-0 z-10 bg-white pb-4 mb-2 border-b border-gray-100">
          <div className="flex justify-center flex-wrap gap-6">
            {(selectedMonth ? [
              { label: 'System', color: chartColors.system },
              { label: 'User', color: chartColors.user },
              { label: 'Actual', color: chartColors.actual }
            ] : [
              { label: 'System Planned', color: chartColors.system },
              { label: 'User Planned', color: chartColors.user },
              { label: 'Actual Completion', color: chartColors.actual }
            ]).map((item, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div 
                  className="w-4 h-3 rounded-sm"
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Scrollable Chart Container */}
        <div className="overflow-x-auto">
          <div 
            style={{ 
              height: chartHeight, 
              width: `${calculatedWidth}px`,
              minWidth: '100%'
            }}
          >
            <Chart
              ref={chartRef}
              type="bar"
              data={getChartConfig()}
              options={chartOptions}
            />
          </div>
        </div>
      </div>
    );
  };

  const CustomSelect = ({ icon: Icon, label, value, onChange, options, className = "" }) => (
    <div className={`relative ${className}`}>
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500">
        <Icon size={18} />
      </div>
      <select
        value={value}
        onChange={onChange}
        className="bg-white border border-gray-300 rounded-lg pl-10 pr-3 py-2.5 w-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm appearance-none"
        aria-label={label}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );

  // Build a full year range for the dropdown
  const minYear = 2020;
  const maxYear = Math.max(
    new Date().getFullYear(),
    ...availableYears.map(y => parseInt(y, 10)).filter(Boolean)
  );
  const fullYearRange = [];
  for (let y = minYear; y <= maxYear; y++) {
    fullYearRange.push(y.toString());
  }

  return (
    <div 
      ref={containerRef}
      className={`p-4 bg-gray-50 ${fullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}
    >
      <div className="mb-6 bg-white p-6 rounded-xl shadow-md border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-5">
          <CustomSelect
            icon={Layers}
            label="Task"
            value={selectedTask}
            onChange={(e) => setSelectedTask(e.target.value)}
            options={tasks.map(task => ({ value: task.id, label: task.name }))}
          />
          <CustomSelect
            icon={Calendar}
            label="Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            options={fullYearRange.map(year => ({ value: year, label: year }))}
          />
          <CustomSelect
            icon={Calendar}
            label="Month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            options={[{ value: "", label: "All Months" }, ...ALL_MONTHS.map(month => ({ value: month, label: month }))]}
          />
           <button 
            onClick={handleExportCSV}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1 text-sm shadow-sm"
            title="Download CSV"
          >
            <FileText size={16} className="mr-1" />
              CSV
          </button>
          <button 
            onClick={handleExportImage}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1 text-sm shadow-sm"
            title="Download PNG"
          >
            <Image size={16} className="mr-1" />
              PNG
          </button>
          <button 
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center space-x-2 text-sm shadow-sm transition-colors"
          >
            <Eye size={16} />
            <span>View Details</span>
          </button>
        </div>

      {renderChart()}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-full w-4/5 max-h-[90vh] overflow-auto shadow-xl border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <Info size={20} className="text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-800">Detailed Progress Data</h2>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-lg hover:bg-gray-100"
              >
                <X size={24} />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Task</th>
                    {selectedMonth ? (
                      <>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">System</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">User</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actual</th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">System Planned</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">User Planned</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actual Completion</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chartData.map((entry, index) => (
                    <tr 
                      key={index} 
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">{entry.task}</td>
                      {selectedMonth ? (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right border-r border-gray-200">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {entry.system}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right border-r border-gray-200">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {entry.user}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              {entry.actual}%
                            </span>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right border-r border-gray-200">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {entry.system_planned}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right border-r border-gray-200">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {entry.user_planned}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              {entry.actual_completion}%
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm shadow-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PhysicalProgressChart;